// Recovery from a console tab left open across a deploy.
//
// THE PRODUCTION FREEZE (proven from the 25.7.2026 access log).
// Vite emits every view as a content-hashed chunk (/assets/Instances-<hash>.js)
// and the router loads them with dynamic import(). A deploy replaces the whole
// dist, so the hashes a long-open tab still references no longer exist — and
// nginx' SPA fallback (`try_files $uri /index.html`) answered those requests
// with index.html: HTTP 200, 476 bytes, Content-Type text/html. The browser
// refuses an HTML payload as a module, import() rejects, vue-router aborts the
// navigation and swallows the error.
//
// The result is exactly what the operator reported as "vse zmrznjeno": clicking
// any nav item does NOTHING, not one request reaches the API — while the
// already-running pollers of the still-mounted view keep the server log looking
// perfectly healthy. Only a manual reload helps. The log shows it verbatim:
//
//   22:07:56 GET /assets/Instances-DHwcTjvn.js  200  476   <- index.html, not JS
//   22:07:57 GET /assets/Users-Bd9QX4ls.js      200  476
//   22:07:58 GET /assets/Mailboxes-BOVAJQnn.js  200  476
//   22:07:58 GET /assets/Support-Dfd7-a6v.js    200  476
//   22:07:59 GET /assets/Settings-C_R1XDKU.js   200  476
//   22:08:18 GET /api/.../mail/status           200  146   <- pollers unaffected
//   22:08:20 GET /api/.../system/status         200 1689
//   22:08:23 GET /dashboard                     200  476   <- manual reload
//
// The fix has two halves:
//   * nginx.conf no longer falls back for /assets/, so a stale chunk is an
//     honest 404 instead of an HTML body pretending to be JavaScript, and
//     index.html is served no-store so a reload really does get the new build;
//   * this guard turns the dead click into the reload the operator had to do by
//     hand — at most once per RELOAD_COOLDOWN_MS (the marker lives in
//     sessionStorage and therefore survives the reload), so a genuinely broken
//     build degrades into one toast instead of a reload loop.

import type { Router } from 'vue-router'

// At most one automatic reload per this window, per tab.
export const RELOAD_COOLDOWN_MS = 30_000

const RELOAD_KEY = 'lastChunkReload'

// Every engine words it differently, and the MIME refusal (our actual case) does
// not mention "chunk" at all — hence the wide net. A false positive costs one
// reload; a false negative costs a frozen console.
const CHUNK_ERROR_MARKERS = [
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'unable to preload css',
    'importing a module script failed',
    'is not a valid javascript mime type',
    'expected a javascript',
    'loading chunk',
    'loading css chunk',
    'text/html',
]

/** isChunkLoadError recognises "the lazy chunk this build asked for is gone". */
export function isChunkLoadError(error: unknown): boolean {
    if (!error) return false

    const message = error instanceof Error
        ? `${error.name} ${error.message}`
        : String(error)

    const haystack = message.toLowerCase()
    return CHUNK_ERROR_MARKERS.some(marker => haystack.includes(marker))
}

/** shouldReload is the loop breaker: one automatic reload per cooldown window. */
export function shouldReload(lastReloadAt: number, now: number): boolean {
    if (!Number.isFinite(lastReloadAt) || lastReloadAt <= 0) return true
    return now - lastReloadAt >= RELOAD_COOLDOWN_MS
}

function session(): Storage | null {
    try {
        return window.sessionStorage
    } catch {
        return null
    }
}

function lastReloadAt(): number {
    const raw = Number(session()?.getItem(RELOAD_KEY))
    return Number.isFinite(raw) ? raw : 0
}

/**
 * installBuildGuard makes a stale-build navigation failure self-healing.
 *
 * onStale is called instead of reloading when the cooldown has not elapsed, so
 * the caller can tell the operator to reload rather than silently doing nothing.
 */
export function installBuildGuard(router: Router, onStale: () => void = () => {}): void {
    const recover = (target?: string) => {
        const now = Date.now()

        if (!shouldReload(lastReloadAt(), now)) {
            onStale()
            return
        }

        session()?.setItem(RELOAD_KEY, String(now))
        // A full load (not router.push) is the point: the tab must fetch the new
        // index.html and with it the new chunk hashes.
        window.location.assign(target || window.location.pathname + window.location.search)
    }

    // The router swallows a rejected lazy import into a navigation failure, so
    // this hook is the only place the freeze is observable from inside the app.
    router.onError((error, to) => {
        if (!isChunkLoadError(error)) return
        recover(to?.fullPath)
    })

    // Vite raises this for a failed module PRELOAD (a <link rel=modulepreload>
    // for a chunk that is gone); unhandled it becomes an uncaught error.
    window.addEventListener('vite:preloadError', event => {
        event.preventDefault()
        recover()
    })
}
